import { useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Clock, User2, Phone, Mail, Edit2, Trash2, X, Maximize2, StickyNote, Receipt, Tag } from 'lucide-react'
import type { Appointment } from '../../api/appointments'
import { Modal } from '../ui/Modal'
import { fullName } from '../../lib/utils'
import { formatDateTime, formatDateOnly } from '../../lib/scheduling'
import { colorOf } from './appointmentColors'
import { sourceLabel } from '../../lib/leadSource'

/** "12 Aug 2026, 09:30 – 10:00" (end time only, same-day) or the all-day date. */
function whenLabel(a: Appointment, style: 'medium' | 'full' = 'medium'): string {
  if (a.isAllDay) return a.allDayDate ? `${formatDateOnly(a.allDayDate, style)} · All day` : 'All day'
  const start = formatDateTime(a.startAt)
  if (!a.endAt) return start
  const endTime = new Date(a.endAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  return `${start} – ${endTime}`
}

// ─── Anchored popup ───────────────────────────────────────────────────────────

interface PopupProps {
  appointment: Appointment
  anchor: { x: number; y: number }
  companyId: string
  onClose: () => void
  onExpand: () => void
  onEdit: () => void
  onDelete: () => void
}

export function AppointmentPopup({
  appointment: a, anchor, companyId, onClose, onExpand, onEdit, onDelete,
}: PopupProps) {
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  const spec = colorOf(a.color)

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        top: Math.min(anchor.y, window.innerHeight - 360),
        left: Math.min(anchor.x, window.innerWidth - 300),
        zIndex: 200,
        width: 288,
      }}
      className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
    >
      <div className="px-4 py-3" style={{ borderLeft: `4px solid ${spec.hex}` }}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <span
              className="inline-block text-xs font-medium px-2 py-0.5 rounded-full mb-1"
              style={{ backgroundColor: spec.hex, color: spec.text }}
            >
              Appointment
            </span>
            <p className="font-semibold text-gray-900 text-sm leading-snug">{a.title}</p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
            <button onClick={onExpand} title="View details" className="text-gray-400 hover:text-gray-600">
              <Maximize2 className="w-4 h-4" />
            </button>
            <button onClick={onClose} title="Close" className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="mt-2 space-y-1.5 text-xs text-gray-500">
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{whenLabel(a)}</span>
          </div>

          {a.lead && (
            <>
              <div className="flex items-center gap-1.5">
                <User2 className="w-3.5 h-3.5 flex-shrink-0" />
                <button
                  onClick={() => { onClose(); navigate(`/company/${companyId}/leads/${a.lead!.id}`) }}
                  className="truncate text-primary-600 hover:underline text-left"
                >
                  {a.lead.name}
                </button>
              </div>
              {a.lead.phone && (
                <div className="flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{a.lead.phone}</span>
                </div>
              )}
              {a.lead.email && (
                <div className="flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{a.lead.email}</span>
                </div>
              )}
              {a.lead.source && (
                <div className="flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{sourceLabel(a.lead.source)}</span>
                </div>
              )}
            </>
          )}

          {a.proforma && (
            <div className="flex items-center gap-1.5">
              <Receipt className="w-3.5 h-3.5 flex-shrink-0" />
              <button
                onClick={() => { onClose(); navigate(`/company/${companyId}/invoices/${a.proforma!.id}/edit`) }}
                className="truncate text-primary-600 hover:underline text-left"
                title="Open this proforma"
              >
                {a.proforma.number}
              </button>
              <span className="ml-auto flex-shrink-0 font-medium text-gray-700">
                {Number(a.proforma.total).toFixed(2)} {a.proforma.currency}
              </span>
            </div>
          )}

          {a.description && <p className="text-gray-600 mt-1 line-clamp-3">{a.description}</p>}

          <div className="pt-1 border-t border-gray-100 text-gray-400">
            Created {formatDateTime(a.createdAt)}
            {a.createdByUser && ` by ${fullName(a.createdByUser.firstName, a.createdByUser.lastName)}`}
          </div>
        </div>
      </div>

      <div className="flex border-t border-gray-100">
        <button
          onClick={onEdit}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
        >
          <Edit2 className="w-3.5 h-3.5" /> Edit
        </button>
        <div className="w-px bg-gray-100" />
        <button
          onClick={onDelete}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs text-red-500 hover:bg-red-50 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      </div>
    </div>
  )
}

// ─── Full detail modal ────────────────────────────────────────────────────────

interface ModalProps {
  appointment: Appointment | null
  companyId: string
  onClose: () => void
  /** Omitted where there is no edit form to open — the dashboard shows it read-only. */
  onEdit?: (a: Appointment) => void
  /** Omitted alongside onEdit; without it the Delete action is hidden too. */
  onDelete?: (id: string) => void
}

export function AppointmentDetailModal({ appointment: a, companyId, onClose, onEdit, onDelete }: ModalProps) {
  const navigate = useNavigate()
  if (!a) return null

  const spec = colorOf(a.color)

  return (
    <Modal open={!!a} onClose={onClose} title="Appointment details" size="lg">
      <div className="p-4 space-y-5">
        <div>
          <span
            className="inline-block text-xs font-medium px-2 py-0.5 rounded-full mb-2"
            style={{ backgroundColor: spec.hex, color: spec.text }}
          >
            {spec.label}
          </span>
          <h3 className="text-lg font-semibold text-gray-900">{a.title}</h3>
          <p className="mt-1 flex items-center gap-2 text-sm text-gray-600">
            <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
            {whenLabel(a, 'full')}
          </p>
        </div>

        {a.lead && (
          <div className="rounded-lg border border-gray-200 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <User2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <button
                onClick={() => { onClose(); navigate(`/company/${companyId}/leads/${a.lead!.id}`) }}
                className="text-sm font-medium text-primary-600 hover:underline text-left"
              >
                {a.lead.name}
              </button>
            </div>
            {a.lead.phone && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <a href={`tel:${a.lead.phone}`} className="hover:underline">{a.lead.phone}</a>
              </div>
            )}
            {a.lead.email && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <a href={`mailto:${a.lead.email}`} className="hover:underline truncate">{a.lead.email}</a>
              </div>
            )}
            {a.lead.source && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Tag className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="truncate">{sourceLabel(a.lead.source)}</span>
              </div>
            )}
          </div>
        )}

        {a.proforma && (
          <div className="rounded-lg border border-gray-200 p-3 flex items-center gap-2">
            <Receipt className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <button
                onClick={() => { onClose(); navigate(`/company/${companyId}/invoices/${a.proforma!.id}/edit`) }}
                className="text-sm font-medium text-primary-600 hover:underline truncate text-left block max-w-full"
                title="Open this proforma"
              >
                {a.proforma.label}
              </button>
              <p className="text-xs text-gray-400 capitalize">{a.proforma.status}</p>
            </div>
            <span className="text-sm font-semibold text-gray-900 flex-shrink-0">
              {Number(a.proforma.total).toFixed(2)} {a.proforma.currency}
            </span>
          </div>
        )}

        {a.description && (
          <div>
            <p className="label flex items-center gap-1.5">
              <StickyNote className="w-3.5 h-3.5 text-gray-400" /> Notes
            </p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{a.description}</p>
          </div>
        )}

        <div className="pt-3 border-t border-gray-100 text-xs text-gray-400">
          Created {formatDateTime(a.createdAt)}
          {a.createdByUser && ` by ${fullName(a.createdByUser.firstName, a.createdByUser.lastName)}`}
        </div>

        <div className="flex justify-between gap-2 pt-1">
          {onDelete ? (
            <button
              onClick={() => onDelete(a.id)}
              className="btn btn-ghost text-red-600 hover:bg-red-50 flex items-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="btn btn-secondary">Close</button>
            {onEdit && (
              <button onClick={() => onEdit(a)} className="btn btn-primary flex items-center gap-1.5">
                <Edit2 className="w-3.5 h-3.5" /> Edit
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}
